import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { useAccount } from 'wagmi'

const WalletConnectReminderContext = createContext(null)

/**
 * Wraps RainbowKit "connect" entry points: shows a short connecting animation, opens the modal
 * immediately (no extra confirmation dialog). English UI.
 */
export function WalletConnectReminderProvider({ children }) {
  const [showConnecting, setShowConnecting] = useState(false)
  const modalWasOpenRef = useRef(false)
  const { connectModalOpen } = useConnectModal()
  const { isConnected, address } = useAccount()

  useEffect(() => {
    if (connectModalOpen) modalWasOpenRef.current = true
  }, [connectModalOpen])

  useEffect(() => {
    if (!showConnecting) return
    if (isConnected && address) {
      setShowConnecting(false)
      modalWasOpenRef.current = false
      return
    }
    if (modalWasOpenRef.current && !connectModalOpen) {
      setShowConnecting(false)
      modalWasOpenRef.current = false
    }
  }, [showConnecting, connectModalOpen, isConnected, address])

  const beginConnect = useCallback((openConnectModal) => {
    if (typeof openConnectModal !== 'function') return
    modalWasOpenRef.current = false
    setShowConnecting(true)
    queueMicrotask(() => {
      try {
        openConnectModal()
      } catch {
        setShowConnecting(false)
      }
    })
  }, [])

  const dismissConnecting = useCallback(() => {
    setShowConnecting(false)
    modalWasOpenRef.current = false
  }, [])

  /** Hide overlay after the RainbowKit modal has opened once, so closing the modal does not flash this screen again. */
  const overlayVisible =
    showConnecting && !connectModalOpen && !isConnected && !modalWasOpenRef.current

  useEffect(() => {
    if (!overlayVisible) return
    const onKey = (e) => {
      if (e.key === 'Escape') dismissConnecting()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [overlayVisible, dismissConnecting])

  return (
    <WalletConnectReminderContext.Provider value={{ beginConnect }}>
      {children}
      {overlayVisible ? (
        <div
          className="fixed inset-0 z-[400] flex min-h-screen cursor-pointer flex-col items-center justify-center gap-5 bg-black/75 p-6 backdrop-blur-sm"
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label="Connecting wallet. Tap to dismiss if nothing opens."
          onClick={dismissConnecting}
        >
          <div className="relative h-16 w-16">
            <div
              className="absolute inset-0 rounded-full border-2 border-cyan-400/25"
              aria-hidden
            />
            <div
              className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-cyan-400 border-r-violet-500"
              style={{ animationDuration: '0.85s' }}
              aria-hidden
            />
            <div
              className="absolute inset-2 animate-pulse rounded-full bg-gradient-to-br from-cyan-500/20 to-violet-600/20 motion-reduce:animate-none"
              aria-hidden
            />
          </div>
          <p className="text-base font-semibold tracking-wide text-white">Connecting wallet…</p>
          <p className="max-w-sm text-center text-sm text-gray-400">
            Choose your wallet in the window that opens next.
          </p>
          <p className="text-xs text-gray-500">
            Tap anywhere to dismiss if the wallet window did not open.
          </p>
        </div>
      ) : null}
    </WalletConnectReminderContext.Provider>
  )
}

export function useBeginWalletConnect() {
  const ctx = useContext(WalletConnectReminderContext)
  if (!ctx) {
    throw new Error('useBeginWalletConnect must be used within WalletConnectReminderProvider')
  }
  return ctx.beginConnect
}
